import React, { useState, useEffect } from "react";
import {
  Sidebar,
  Schedule,
  useValidation,
  hourValidator,
  minuteValidator,
  selectedDayValidator,
  dayValidator,
  Button,
  cronValidator,
  ScheduleInfo,
} from "@ssce/ui-shared-library";
import {
  RadioContainer,
  CreateAwaContainer,
  Text,
  Label,
  Header,
} from "./createWorkflowSidebars.styles";
import Radio from "@mui/material/Radio";

type SidebarProps = {
  onSubmit: (data: any) => void;
  onClose: () => void;
  isOpen: boolean;
};

export const ScheduleSidebar: React.FC<
  SidebarProps & {
    initScheduleInfo: ScheduleInfo;
  }
> = ({ initScheduleInfo, onSubmit, isOpen, onClose }) => {
  const [scheduleInfo, setScheduleInfo] =
    useState<ScheduleInfo>(initScheduleInfo);
  const { errors, validateField, clearErrors, validateForm } = useValidation();

  useEffect(() => {
    setScheduleInfo(initScheduleInfo);
  }, [initScheduleInfo]);

  const validators = () => {
    if (scheduleInfo.mvSchedule === "noSchedule") return [];
    if (scheduleInfo.mvSchedule === "daily") {
      return [hourValidator, minuteValidator];
    }
    if (scheduleInfo.mvSchedule === "weekly") {
      return [selectedDayValidator, hourValidator, minuteValidator];
    }
    if (scheduleInfo.mvSchedule === "monthly") {
      return [dayValidator, hourValidator, minuteValidator];
    }
    if (scheduleInfo.mvSchedule === "cron") {
      return [cronValidator];
    }
    return [];
  };

  const handleScheduleUpdate = (freq, key, value) => {
    validateField(key, value, validators());
    setScheduleInfo((prevData) => ({
      ...prevData,
      [freq]: { ...prevData[freq], [key]: value },
    }));
  };

  const handleFreqButtonClick = (sched) => {
    clearErrors();
    setScheduleInfo((prevData) => ({
      ...prevData,
      ...INITIAL_SCHEDULE_INFO,
      mvSchedule: sched,
    }));
  };

  return (
    <Sidebar
      isOpen={isOpen}
      title={"Workflow Schedule"}
      width={"fit-content"}
      onClose={() => onClose()}
      footer={
        <>
          <Button
            size={"large"}
            variant={"secondary"}
            id={"cancel"}
            onClick={() => onClose()}
          >
            Cancel
          </Button>

          <Button
            size={"large"}
            variant={"default"}
            id={"save"}
            onClick={() => {
              if (scheduleInfo.mvSchedule) {
                let isValid =
                  scheduleInfo.mvSchedule === "noSchedule"
                    ? true
                    : validateForm(
                        scheduleInfo[scheduleInfo.mvSchedule],
                        validators(),
                      );
                if (isValid) onSubmit(scheduleInfo);
              }
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <Schedule
        showTooltip={false}
        handleFreqButtonClick={handleFreqButtonClick}
        handleFormUpdate={handleScheduleUpdate}
        errors={errors}
        freqSelected={scheduleInfo.mvSchedule ?? "noSchedule"}
        scheduleInfo={scheduleInfo}
      />
    </Sidebar>
  );
};

export type AwaData = {
  sleeves?: "yes" | "no";
  transactionTiming?: "tradeDate" | "settlementDate";
};

export const CreateAwaInputDataSidebar: React.FC<
  SidebarProps & {
    initAwaData: AwaData;
  }
> = ({ initAwaData, onClose, onSubmit, isOpen }) => {
  const [awaData, setAwaData] = useState<AwaData>(initAwaData);

  useEffect(() => {
    setAwaData(initAwaData);
  }, [initAwaData]);

  return (
    <Sidebar
      isOpen={isOpen}
      title={"Create AWA Performance Input Data"}
      onClose={() => onClose()}
      footer={
        <>
          <Button
            size={"large"}
            variant={"secondary"}
            id={"cancel"}
            onClick={() => onClose()}
          >
            Cancel
          </Button>

          <Button
            size={"large"}
            variant={"default"}
            id={"save"}
            onClick={() => onSubmit(awaData)}
          >
            Save
          </Button>
        </>
      }
    >
      <CreateAwaContainer>
        <Header>Select Preferences for Accurate Data Processing.</Header>
        <Label>Sleeves</Label>
        <RadioContainer>
          <Radio
            id={"sleevesYes"}
            data-testid={"sleevesYes"}
            value={"yes"}
            checked={awaData.sleeves === "yes"}
            onChange={() =>
              setAwaData({
                ...awaData,
                sleeves: "yes",
              })
            }
          />
          <Text>Yes</Text>
          <Radio
            id={"sleevesNo"}
            value={"no"}
            data-testid={"sleevesNo"}
            checked={awaData.sleeves === "no"}
            onChange={() =>
              setAwaData({
                ...awaData,
                sleeves: "no",
              })
            }
          />
          <Text>No</Text>
        </RadioContainer>
        <Label>Transaction Timing</Label>
        <RadioContainer>
          <Radio
            id={"transactionTradeDate"}
            value={"tradeDate"}
            data-testid={"transactionTradeDate"}
            checked={awaData.transactionTiming === "tradeDate"}
            onChange={() =>
              setAwaData({
                ...awaData,
                transactionTiming: "tradeDate",
              })
            }
          />
          <Text>Trade Date</Text>
          <Radio
            id={"transactionSettlementDate"}
            data-testid={"transactionSettlementDate"}
            value={"settlementDate"}
            checked={awaData.transactionTiming === "settlementDate"}
            onChange={() =>
              setAwaData({
                ...awaData,
                transactionTiming: "settlementDate",
              })
            }
          />
          <Text>Settlement Date</Text>
        </RadioContainer>
      </CreateAwaContainer>
    </Sidebar>
  );
};

const INITIAL_SCHEDULE_INFO = {
  weekly: {
    selectedDatesOfWeek: [],
    HR: "",
    MIN: "",
  },
  monthly: {
    monthlyFreqDay: "",
    HR: "",
    MIN: "",
  },
  cron: {
    cronExpression: "",
  },
  daily: {
    HR: "",
    MIN: "",
  },
  mvSchedule: "daily",
};
